import { Subject } from "rxjs";
import { Service } from "..";
import { EventableService, makeServiceEvent, RbServiceEvent } from "../service";
import { RBEvent } from "../../models";

/**
 * @internal
 */
export const LOGGING_SVC = 'LoggingService';

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';

interface LoggingData {
    metadata?: Record<string, unknown>;
}

export interface LoggingEntry extends LoggingData {
    type: LogLevel;
    message: string;
}

export interface LoggingEvent {
    LOG: LoggingEntry;
}

export type LoggingService = EventableService<LoggingEvent>;

/**
 * @internal
 */
export class LoggingServiceRB extends Service implements LoggingService {
    private readonly $subject = new Subject<RbServiceEvent<LoggingEvent>>();

    public static getInstance() {
        return Service.create(LOGGING_SVC, LoggingServiceRB);
    }

    /**
     * @internal
     */
    public static build() {
        return new LoggingServiceRB();
    }

    private constructor() {
        super();
    }

    public events<const EventNames extends keyof LoggingEvent>(...eventNames: EventNames[]) {
        return makeServiceEvent(this.$subject, ...eventNames);
    }

    /**
     * @internal
     */
    public error(msg: string, data?: LoggingData) {
        this.sendEvent('LOG', {
            type: 'error',
            message: msg,
            ...data
        });
    }

    /**
     * @internal
     */
    public warn(msg: string, data?: LoggingData) {
        this.sendEvent('LOG', {
            type: 'warn',
            message: msg,
            ...data
        });
    }

    /**
     * @internal
     */
    public info(msg: string, data?: LoggingData) {
        this.sendEvent('LOG', {
            type: 'info',
            message: msg,
            ...data
        });
    }

    /**
     * @internal
     */
    public debug(msg: string, data?: LoggingData) {
        this.sendEvent('LOG', {
            type: 'debug',
            message: msg,
            ...data
        });
    }

    /**
     * @internal
     */
    public trace(msg: string, data?: LoggingData) {
        this.sendEvent('LOG', {
            type: 'trace',
            message: msg,
            ...data
        });
    }

    /**
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     * FavoritesService events send to other services via sendEvent()
     * Can listen to it via FavoritesServiceRB.subscribe() API
     */
    private sendEvent<const EventName extends keyof LoggingEvent>(eventName: EventName, data: LoggingEvent[EventName]): void {
        this.$subject.next(RBEvent.create(eventName, data));
    }
}

export function createBasicLogger() {
    return LoggingServiceRB.getInstance().events('LOG').subscribe((event) => {
        const { type, msg, metadata } = event.data;
        switch (type) {
            case 'trace':
                console.trace('%s %o', msg, metadata);
                break;
            case 'debug':
                console.debug('%s %o', msg, metadata);
                break;
            case 'info':
                console.info('%s %o', msg, metadata);
                break;
            case 'warn':
                console.warn('%s %o', msg, metadata);
                break;
            case 'error':
                console.error('%s %o', msg, metadata);
                break;
        }
    });
}
